import { nanoid } from '@blocksuite/store';
import zod from 'zod';

import { getTagColor } from '../../core/component/tags/colors.js';
import { t } from '../../core/index.js';
import { propertyType } from '../../core/property/property-config.js';
import { SelectPropertySchema } from '../select/define.js';
export const multiSelectPropertyType = propertyType('multi-select');

export const multiSelectPropertyModelConfig =
  multiSelectPropertyType.modelConfig({
    name: 'Multi-select',
    propertyData: {
      schema: SelectPropertySchema,
      default: () => ({
        options: [],
      }),
    },
    jsonValue: {
      schema: zod.array(zod.string()).nullable(),
      isEmpty: ({ value }) => !Array.isArray(value) || value.length === 0,
      type: ({ data }) => t.array.instance(t.tag.instance(data.options)),
    },
    rawValue: {
      schema: zod.array(zod.string()),
      default: () => [],
      toString: ({ value, data }) => {
        if (!Array.isArray(value)) return '';
        const options = data?.options;
        return value
          .map(id =>
            Array.isArray(options)
              ? (options.find(v => v.id === id)?.value ?? '')
              : ''
          )
          .join(',');
      },
      fromString: ({ value: oldValue, data }) => {
        if (!oldValue.trim()) {
          return {
            value: [],
            data: data,
          };
        }

        const optionMap = Object.fromEntries(
          data.options.map(v => [v.value, v])
        );
        const optionNames = oldValue
          .split(',')
          .map(v => v.trim())
          .filter(v => v);

        // New column data: the stored options stay untouched until written.
        const options = [...data.options];
        const value: string[] = [];
        optionNames.forEach(name => {
          let option = optionMap[name];
          if (!option) {
            option = { id: nanoid(), value: name, color: getTagColor() };
            optionMap[name] = option;
            options.push(option);
          }
          value.push(option.id);
        });

        return {
          value,
          data: { ...data, options },
        };
      },
      toJson: ({ value }) => (Array.isArray(value) ? value : []),
      fromJson: ({ value }) =>
        Array.isArray(value) && value.every(v => typeof v === 'string')
          ? value
          : [],
    },
    addGroup: ({ text, oldData }) => {
      return {
        options: [
          ...(oldData.options ?? []),
          {
            id: nanoid(),
            value: text,
            color: getTagColor(),
          },
        ],
      };
    },
  });
